<template>
  <TransitionRoot as="template" :show="show && !showInvoicesModal">
    <Dialog as="div" class="fixed inset-0 z-50 overflow-hidden" @close="emitClose">
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0 bg-black bg-opacity-40 backdrop-soft" />

        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-4 sm:pl-8">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-400 sm:duration-500"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="pointer-events-auto w-screen max-w-xl">
              <form class="flex h-full flex-col modalShell">
                <!-- HEADER -->
                <div class="shrink-0 modalHeader">
                  <div class="flex items-start justify-between gap-4">
                    <div class="min-w-0">
                      <DialogTitle class="text-lg font-semibold tracking-tight text-white">
                        Dodaj zamówienie paliwowe
                      </DialogTitle>
                      <p class="mt-1 text-sm text-slate-300">
                        Utwórz nowe <span class="font-semibold text-white">zamówienie</span> na dostawę paliwa.
                      </p>
                    </div>

                    <button
                      type="button"
                      @click.prevent="emitClose"
                      class="iconBtn shrink-0"
                      aria-label="Zamknij panel"
                    >
                      <svg class="h-4 w-4 text-slate-200" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- BODY -->
                <div class="min-h-0 flex-1 overflow-y-auto">
                  <div class="space-y-4 p-4 sm:p-5">
                    <!-- SOURCE -->
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Klient</div>
                          <div class="sectionSub">Wybierz klienta z systemu lub wprowadź dane ręcznie.</div>
                        </div>
                      </div>

                      <div class="sectionBody">
                        <div class="inline-flex rounded-2xl border border-slate-700 bg-slate-900 bg-opacity-50 p-1 shadow-inner overflow-hidden">
                          <button
                            type="button"
                            @click.prevent="clientSource = 'existing'"
                            :class="[
                              'tabSwitchBtn',
                              clientSource === 'existing' ? 'tabSwitchBtn--active' : ''
                            ]"
                          >
                            Klient systemowy
                          </button>
                          <button
                            type="button"
                            @click.prevent="clientSource = 'manual'"
                            :class="[
                              'tabSwitchBtn',
                              clientSource === 'manual' ? 'tabSwitchBtn--active' : ''
                            ]"
                          >
                            Klient spoza systemu
                          </button>
                        </div>

                        <p class="mt-3 text-xs text-slate-400">
                          W trybie klienta systemowego dane podstawowe i adres dostawy mogą zostać automatycznie uzupełnione.
                        </p>

                        <p v-if="errors.clientExisting" class="mt-2 text-xs text-red-400">
                          Wybierz klienta z listy.
                        </p>
                      </div>
                    </section>

                    <!-- EXISTING CLIENT -->
                    <section v-if="clientSource === 'existing'" class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Klient systemowy</div>
                          <div class="sectionSub">Wybierz firmę z bazy użytkowników.</div>
                        </div>
                      </div>

                      <div class="sectionBody">
                        <div class="fieldLabel">Wybierz firmę</div>

                        <Listbox v-model="pickedUser" as="div" class="mt-2">
                          <div class="relative">
                            <ListboxButton class="selectBtn">
                              <span class="flex items-center" v-if="pickedUser">
                                <span class="inline-block h-2 w-2 flex-shrink-0 rounded-full bg-green-400" />
                                <span class="ml-3 block truncate text-slate-100">
                                  {{ pickedUser.company?.name || 'Brak nazwy firmy' }}
                                </span>
                              </span>

                              <span v-else class="flex items-center">
                                <span class="inline-block h-2 w-2 flex-shrink-0 rounded-full bg-slate-500" />
                                <span class="ml-3 block truncate text-slate-400">
                                  Wybierz klienta
                                </span>
                              </span>

                              <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                <ChevronDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                              </span>
                            </ListboxButton>

                            <transition
                              leave-active-class="transition ease-in duration-100"
                              leave-from-class="opacity-100"
                              leave-to-class="opacity-0"
                            >
                              <ListboxOptions class="selectOptions">
                                <ListboxOption
                                  v-for="person in usersList"
                                  :key="person.id"
                                  :value="person"
                                  v-slot="{ active, selected }"
                                  @click.stop="applyUserToClient(person)"
                                  as="template"
                                >
                                  <li
                                    :class="[
                                      active ? 'bg-slate-700 text-white' : 'text-slate-200',
                                      'relative cursor-default select-none py-3 pl-3 pr-9'
                                    ]"
                                  >
                                    <div class="flex items-center">
                                      <span class="inline-block h-2 w-2 flex-shrink-0 rounded-full bg-green-400" aria-hidden="true" />
                                      <span
                                        :class="[
                                          selected ? 'font-semibold' : 'font-normal',
                                          'ml-3 block truncate'
                                        ]"
                                      >
                                        {{ person.company?.name || 'Brak nazwy firmy' }}
                                      </span>
                                    </div>

                                    <span
                                      v-if="selected"
                                      :class="[
                                        active ? 'text-white' : 'text-blue-300',
                                        'absolute inset-y-0 right-0 flex items-center pr-4'
                                      ]"
                                    >
                                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  </li>
                                </ListboxOption>

                                <li
                                  v-if="usersList.length === 0"
                                  class="relative cursor-default select-none py-3 pl-3 pr-9 text-xs text-slate-400"
                                >
                                  Brak klientów w bazie.
                                </li>
                              </ListboxOptions>
                            </transition>
                          </div>
                        </Listbox>

                        <div v-if="pickedUser" class="mt-4 infoGrid">
                          <div class="infoBox">
                            <div class="infoLabel">Dane fakturowe</div>
                            <div class="infoValue">{{ pickedUser.company?.name }}</div>
                            <div class="infoMuted">
                              {{ pickedUser.company?.address }}, {{ pickedUser.company?.postCode }} {{ pickedUser.company?.city }}
                            </div>
                            <div class="infoMuted">NIP: {{ pickedUser.company?.nip }}</div>
                          </div>

                          <div class="infoBox">
                            <div class="infoLabel">Kontakt</div>
                            <div class="infoMuted">E-mail: {{ pickedUser.contact?.email }}</div>
                            <div class="infoMuted">Telefon: {{ pickedUser.contact?.phone }}</div>
                          </div>
                        </div>

                        <!-- RATING PŁATNICZY -->
                        <div v-if="pickedUser" class="mt-4 ratingCard">
                          <div class="ratingTop">
                            <div class="min-w-0">
                              <div class="ratingHeading">Rating płatniczy</div>
                              <div class="ratingSub" v-if="!clientInvoicesLoading && !clientRating.hasHistory">
                                Rating zostanie wyliczony po pojawieniu się historii faktur.
                              </div>
                              <div class="ratingSub" v-else-if="clientInvoicesLoading">
                                Pobieranie historii faktur…
                              </div>
                              <div class="ratingSub" v-else>
                                Oparty na terminowości, liczbie i kwocie zaległości.
                              </div>
                            </div>

                            <button
                              type="button"
                              class="ratingBtn shrink-0"
                              :disabled="clientInvoicesLoading"
                              @click.prevent="showInvoicesModal = true"
                            >
                              <span v-if="clientInvoicesLoading">Wczytywanie…</span>
                              <span v-else>Zobacz faktury ({{ clientInvoices.length }})</span>
                            </button>
                          </div>

                          <div class="ratingStarsRow">
                            <div class="starsWrap" :class="{ 'starsWrap--empty': !clientRating.hasHistory }">
                              <svg
                                v-for="n in 5"
                                :key="n"
                                viewBox="0 0 20 20"
                                class="starIcon"
                                :class="{ 'starIcon--on': clientRating.hasHistory && n <= (clientRating.paymentRatingStars || 0) }"
                                aria-hidden="true"
                              >
                                <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.78L10 14.77l-5.2 2.73.99-5.78L1.58 7.62l5.82-.85L10 1.5z" />
                              </svg>
                            </div>
                            <div class="ratingValue">
                              <span v-if="clientInvoicesLoading">—</span>
                              <span v-else-if="!clientRating.hasHistory">Brak ratingu</span>
                              <span v-else>
                                {{ clientRating.paymentRatingStars }}/5
                                <span class="ratingValueHint">({{ clientRating.paymentRatingScore }} pkt)</span>
                              </span>
                            </div>
                          </div>

                          <div class="ratingGrid">
                            <div class="metricBox">
                              <div class="metricLabel">Nieopłacone faktury</div>
                              <div class="metricValue">
                                {{ clientRating.hasHistory ? clientRating.unpaidInvoicesCount : '—' }}
                              </div>
                              <div class="metricSub" v-if="clientRating.hasHistory && clientRating.overdueInvoicesCount > 0">
                                w tym {{ clientRating.overdueInvoicesCount }} po terminie
                              </div>
                            </div>
                            <div class="metricBox">
                              <div class="metricLabel">Kwota przeterminowana</div>
                              <div
                                class="metricValue"
                                :class="{ 'metricValue--danger': clientRating.hasHistory && clientRating.overdueAmount > 0 }"
                              >
                                {{ clientRating.hasHistory ? formatAmountPLN(clientRating.overdueAmount) : '—' }}
                              </div>
                            </div>
                            <div class="metricBox">
                              <div class="metricLabel">Średnie opóźnienie</div>
                              <div class="metricValue">
                                {{ clientRating.hasHistory ? formatAverageDelay(clientRating.averageDelayDays) : '—' }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    <!-- MANUAL CLIENT -->
                    <section v-else class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Klient spoza systemu</div>
                          <div class="sectionSub">Uzupełnij dane firmy ręcznie.</div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-4">
                        <div class="fieldWrap">
                          <label class="fieldLabel">Nazwa firmy*</label>
                          <input
                            v-model="form.clientManual.invoiceAddress.name"
                            type="text"
                            maxlength="100"
                            :class="inputCls(errors.clientName)"
                            placeholder="np. WB Tank Sp. z o.o."
                          />
                          <p v-if="errors.clientName" class="fieldError">Podaj nazwę firmy.</p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">NIP*</label>
                          <input
                            v-model="form.clientManual.invoiceAddress.nip"
                            type="text"
                            maxlength="20"
                            :class="inputCls(errors.clientNip)"
                            placeholder="np. 8421761290"
                          />
                          <p v-if="errors.clientNip" class="fieldError">Podaj NIP.</p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Adres*</label>
                          <input
                            v-model="form.clientManual.invoiceAddress.address"
                            type="text"
                            maxlength="100"
                            :class="inputCls(errors.clientAddress)"
                            placeholder="np. ul. Targowa 9"
                          />
                          <p v-if="errors.clientAddress" class="fieldError">Podaj adres.</p>
                        </div>

                        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div class="fieldWrap">
                            <label class="fieldLabel">Kod pocztowy*</label>
                            <input
                              v-model="form.clientManual.invoiceAddress.postCode"
                              type="text"
                              maxlength="20"
                              :class="inputCls(errors.clientPostCode)"
                              placeholder="np. 63-500"
                            />
                            <p v-if="errors.clientPostCode" class="fieldError">Podaj kod pocztowy.</p>
                          </div>

                          <div class="fieldWrap">
                            <label class="fieldLabel">Miejscowość*</label>
                            <input
                              v-model="form.clientManual.invoiceAddress.city"
                              type="text"
                              maxlength="60"
                              :class="inputCls(errors.clientCity)"
                              placeholder="np. Ostrzeszów"
                            />
                            <p v-if="errors.clientCity" class="fieldError">Podaj miejscowość.</p>
                          </div>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">E-mail kontaktowy*</label>
                          <input
                            v-model="form.clientManual.contact.email"
                            type="email"
                            maxlength="80"
                            :class="inputCls(errors.clientEmail)"
                            placeholder="np. biuro@firma.pl"
                          />
                          <p v-if="errors.clientEmail" class="fieldError">Podaj poprawny adres e-mail.</p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Telefon kontaktowy*</label>
                          <input
                            v-model="form.clientManual.contact.phone"
                            type="text"
                            maxlength="20"
                            :class="inputCls(errors.clientPhone)"
                            placeholder="np. 600700800"
                          />
                          <p v-if="errors.clientPhone" class="fieldError">Podaj numer telefonu (min. 5 znaków).</p>
                        </div>
                      </div>
                    </section>

                    <!-- DELIVERY -->
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Adres dostawy</div>
                          <div class="sectionSub">
                            Przy kliencie systemowym adres może zostać skopiowany z danych fakturowych.
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-4">
                        <!-- Selektor zapisanego adresu -->
                        <div class="fieldWrap" v-if="clientSource === 'existing' && pickedUser?.deliveryAddresses?.length > 0">
                          <label class="fieldLabel">Wybierz adres dostawy</label>
                          <select
                            :value="selectedDeliveryAddressIndex"
                            @change="selectDeliveryAddress($event.target.value === '' ? null : Number($event.target.value))"
                            class="selectField"
                          >
                            <option :value="''">Wpisz ręcznie</option>
                            <option
                              v-for="(addr, idx) in pickedUser.deliveryAddresses"
                              :key="idx"
                              :value="idx"
                            >
                              {{ addr.label ? addr.label + ' — ' : '' }}{{ addr.city }}, {{ addr.address }}{{ addr.regionName ? ' (' + addr.regionName + ')' : '' }}
                            </option>
                          </select>
                        </div>

                        <p v-if="selectedDeliveryAddressIndex !== null" class="pickedFromListHint">
                          Wybrany z listy. Zmień przez „Wpisz ręcznie" powyżej.
                        </p>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Adres*</label>
                          <input
                            v-model="form.deliveryAddress.address"
                            type="text"
                            maxlength="100"
                            :disabled="selectedDeliveryAddressIndex !== null"
                            :class="inputCls(errors.deliveryAddressField)"
                            placeholder="np. Targowa 9"
                          />
                          <p v-if="errors.deliveryAddressField" class="fieldError">{{ errors.deliveryAddressMessage }}</p>
                        </div>

                        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                          <div class="fieldWrap">
                            <label class="fieldLabel">Kod pocztowy*</label>
                            <input
                              v-model="form.deliveryAddress.postCode"
                              type="text"
                              maxlength="20"
                              :disabled="selectedDeliveryAddressIndex !== null"
                              :class="inputCls(errors.deliveryPostCode)"
                              placeholder="np. 63-500"
                            />
                            <p v-if="errors.deliveryPostCode" class="fieldError">Podaj kod pocztowy.</p>
                          </div>

                          <div class="fieldWrap">
                            <label class="fieldLabel">Miejscowość*</label>
                            <input
                              v-model="form.deliveryAddress.city"
                              type="text"
                              maxlength="60"
                              :disabled="selectedDeliveryAddressIndex !== null"
                              :class="inputCls(errors.deliveryCity)"
                              placeholder="np. Ostrzeszów"
                            />
                            <p v-if="errors.deliveryCity" class="fieldError">Podaj miejscowość.</p>
                          </div>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Region dostawy</label>
                          <select
                            v-model="form.deliveryAddress.regionName"
                            :disabled="selectedDeliveryAddressIndex !== null"
                            class="selectField"
                          >
                            <option :value="''">Nie przypisano</option>
                            <option
                              v-for="region in regions"
                              :key="region.id"
                              :value="region.name"
                            >
                              {{ region.name }} – {{ region.description }}
                            </option>
                          </select>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Weryfikacja adresu (Google Maps)</label>
                          <div v-if="deliveryVerification.loading" class="addrVerifyBox addrVerifyLoading">
                            <div class="flex items-center gap-2">
                              <svg class="animate-spin h-4 w-4 text-slate-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
                              </svg>
                              <span>Weryfikowanie adresu w Google Maps…</span>
                            </div>
                          </div>
                          <div v-else-if="deliveryVerification.error" class="addrVerifyBox addrVerifyError">
                            <p class="font-semibold">Nie udało się zweryfikować adresu</p>
                            <p class="mt-1 text-xs opacity-90">{{ deliveryVerification.error }}</p>
                          </div>
                          <div v-else-if="deliveryVerification.coordinates" class="addrVerifyBox addrVerifySuccess">
                            <p class="text-xs font-semibold text-emerald-300">Adres zweryfikowany ✓</p>
                            <p class="mt-1 text-xs text-slate-200" v-if="deliveryVerification.formattedAddress">
                              Google: <span class="font-semibold text-white">{{ deliveryVerification.formattedAddress }}</span>
                            </p>
                            <p class="mt-0.5 text-[11px] text-slate-400">
                              Koordynaty: {{ deliveryVerification.coordinates.latitude }}, {{ deliveryVerification.coordinates.longitude }}
                            </p>
                            <div v-if="deliveryVerification.mapEmbedUrl" class="mt-3 overflow-hidden" style="border-radius: 14px; border: 1px solid rgba(71,85,105,0.6);">
                              <iframe
                                :src="deliveryVerification.mapEmbedUrl"
                                width="100%"
                                height="220"
                                style="border: 0; display: block;"
                                loading="lazy"
                                referrerpolicy="no-referrer-when-downgrade"
                                allowfullscreen
                              ></iframe>
                            </div>
                            <p class="mt-2 text-[11px] text-slate-400">
                              Sprawdź na mapie czy pin stoi w dobrym miejscu. Jeśli nie — popraw adres powyżej.
                            </p>
                          </div>
                          <p v-else class="text-[11px] text-slate-500">
                            Wpisz adres i miasto — mapka pojawi się automatycznie.
                          </p>
                        </div>

                        <div class="fieldWrap">
  <label class="fieldLabel">Preferowany termin dostawy*</label>

  <div class="datePickerWrap">
    <VueDatePicker
      v-model="deliveryRange"
      range
      auto-apply
      locale="pl"
      :format="'dd.MM.yyyy'"
      :enable-time-picker="false"
      :clearable="false"
      :min-date="todayStartOfDay"
      :prevent-min-max-navigation="true"
      placeholder="Wybierz zakres dat dostawy..."
    />
  </div>

  <p class="fieldHint">
    Wybierz <span class="font-semibold text-slate-200">zakres dat</span>, w którym klient chciałby otrzymać dostawę.
  </p>
  <p v-if="errors.deliveryDate" class="fieldError">
    Wybierz poprawny przedział dat (od – do).
  </p>
</div>
                      </div>
                    </section>

                    <!-- INVOICE / STATUS -->
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Faktura i status</div>
                          <div class="sectionSub">Ustal dostawcę i stan początkowy zamówienia.</div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-4">
                        <div class="fieldWrap">
                          <label class="fieldLabel">Dostawca*</label>
                          <select
                            v-model="form.invoice.isLauronInvoice"
                            class="selectField"
                          >
                            <option :value="true">Lauron</option>
                            <option :value="false">WBtank</option>
                          </select>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Zamówienie przyjęte do realizacji*</label>
                          <select
                            v-model="form.acceptedByAdmin"
                            class="selectField"
                          >
                            <option :value="false">Nie (wymaga późniejszej akceptacji)</option>
                            <option :value="true">Tak</option>
                          </select>
                          <p class="fieldHint">
                            Dopóki zamówienie nie zostanie zaakceptowane, nie będzie mogło zostać zrealizowane.
                          </p>
                        </div>
                      </div>
                    </section>

                    <!-- FUEL -->
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Zamówione paliwo</div>
                          <div class="sectionSub">Wybierz produkt i podaj ilość.</div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-4">
                        <div class="fieldWrap">
                          <label class="fieldLabel">Produkt*</label>
                          <select
                            v-model.number="form.orderedFuel.fuelId"
                            class="selectField"
                          >
                            <option :value="null" disabled>Wybierz produkt</option>
                            <option
                              v-for="fuel in fuels"
                              :key="fuel.id"
                              :value="fuel.id"
                            >
                              {{ fuel.name }}
                            </option>
                          </select>
                          <p v-if="errors.fuel" class="fieldError">Wybierz produkt paliwowy.</p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Ilość (L)*</label>
                          <input
                            type="number"
                            min="1"
                            max="99999"
                            step="1"
                            v-model="form.orderedFuel.quantityLiters"
                            :class="inputCls(errors.quantityLiters)"
                            placeholder="np. 5000"
                          />
                          <p v-if="errors.quantityLiters" class="fieldError">
                            Podaj ilość w zakresie 1 – 99&nbsp;999 L.
                          </p>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>

                <!-- FOOTER -->
                <div class="shrink-0 border-t border-slate-700 bg-slate-800 bg-opacity-95 px-4 py-4">
                  <div class="flex items-center justify-between gap-3">
                    <div class="text-xs text-slate-400">
                      <span v-if="loading" class="text-slate-200">Zapisywanie danych...</span>
                      <span v-else>Sprawdź dane klienta, adres dostawy i parametry paliwa.</span>
                    </div>

                    <div class="flex items-center gap-2">
                      <button
                        @click.prevent="emitClose"
                        v-if="!loading"
                        type="button"
                        class="btnSecondary"
                      >
                        Zamknij
                      </button>

                      <button
                        @click.prevent="validateAndSave"
                        v-if="!loading"
                        type="button"
                        class="btnPrimary"
                      >
                        Dodaj zamówienie
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <ClientInvoicesModal
    v-if="showInvoicesModal"
    :open="showInvoicesModal"
    :invoices="clientInvoices"
    :loading="clientInvoicesLoading"
    :company-name="pickedUser?.company?.name || ''"
    @close="showInvoicesModal = false"
  />
</template>

<script>
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
  Listbox,
  ListboxButton,
  ListboxOptions,
  ListboxOption
} from '@headlessui/vue'
import { CheckIcon, ChevronDownIcon } from '@heroicons/vue/24/solid'
import { db } from '@/firebase/gfbconf.js'
import axios from 'axios'
import VueDatePicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import ClientInvoicesModal from '@/components/ClientInvoicesModal.vue'
import { computePaymentRating, formatAmountPLN, formatAverageDelay } from '@/utils/paymentRating.js'
import { isValidDeliveryAddress } from '@/services/sentApi'

export default {
  name: 'ModalNewFuelOrder',
  components: {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    Listbox,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
    CheckIcon,
    ChevronDownIcon,
    VueDatePicker,
    ClientInvoicesModal
  },
  props: {
    show: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      clientSource: 'existing',
      usersList: [],
      pickedUser: null,
      clientInvoices: [],
      clientInvoicesLoading: false,
      clientInvoicesUserId: null,
      showInvoicesModal: false,
      selectedDeliveryAddressIndex: null,
      fuels: [
        { id: 30, name: 'Shell FuelSave Diesel CN 27102011' }
      ],
      regions: [],
      deliveryRange: null,
      form: {
        acceptedByAdmin: true,
        clientManual: {
          contact: {
            email: '',
            invoiceEmail: '',
            phone: ''
          },
          invoiceAddress: {
            address: '',
            city: '',
            name: '',
            nip: '',
            postCode: ''
          }
        },
        deliveryAddress: {
          address: '',
          city: '',
          postCode: '',
          regionName: ''
        },
        invoice: {
          isLauronInvoice: true
        },
        orderedFuel: {
          fuelId: 30,
          quantityLiters: null
        }
      },
      deliveryVerification: {
        loading: false,
        coordinates: null,           // { latitude, longitude } po udanym geocoding
        formattedAddress: null,      // adres sformatowany przez Google
        mapEmbedUrl: null,           // URL do iframe mapy
        error: null,                 // komunikat błędu jeśli geocoding nieudany
        verifiedFor: null            // klucz 'address|city|postCode' dla którego ostatnio się udało
      },
      geocodeDebounceTimer: null,
      errors: {
        clientExisting: false,
        clientName: false,
        clientNip: false,
        clientAddress: false,
        clientPostCode: false,
        clientCity: false,
        clientEmail: false,
        clientPhone: false,
        deliveryAddressField: false,
        deliveryAddressMessage: 'Podaj adres dostawy.',
        deliveryPostCode: false,
        deliveryCity: false,
        fuel: false,
        quantityLiters: false,
        deliveryDate: false
      }
    }
  },
  watch: {
    clientSource(newVal) {
      if (newVal === 'manual') {
        this.form.deliveryAddress.address = ''
        this.form.deliveryAddress.city = ''
        this.form.deliveryAddress.postCode = ''
        this.resetDeliveryVerification()
      } else if (newVal === 'existing' && this.pickedUser) {
        this.applyUserToClient(this.pickedUser)
      }
    },
    'form.deliveryAddress.address'() { this.scheduleGeocode() },
    'form.deliveryAddress.city'() { this.scheduleGeocode() },
    'form.deliveryAddress.postCode'() { this.scheduleGeocode() }
  },
  computed: {
    todayStartOfDay() {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      return d
    },
    clientRating() {
      return computePaymentRating(this.clientInvoices || [], new Date())
    }
  },
  created() {
    this.downloadUsers()
    this.loadRegions()
  },
  methods: {
    formatAmountPLN,
    formatAverageDelay,

    inputCls(hasError) {
      return [
        'inputField',
        hasError ? 'inputFieldError' : ''
      ]
    },

    async loadInvoicesForUser(userId) {
      if (!userId) {
        this.clientInvoices = []
        this.clientInvoicesUserId = null
        this.clientInvoicesLoading = false
        return
      }
      // Pomiń ponowne pobieranie jeśli mamy aktualne dane dla tego samego użytkownika.
      if (this.clientInvoicesUserId === userId && !this.clientInvoicesLoading) return

      this.clientInvoicesLoading = true
      this.clientInvoicesUserId = userId
      try {
        const snap = await db.collection('Invoices')
          .where('active', '==', true)
          .where('payerId', '==', userId)
          .get()
        const list = []
        snap.forEach((doc) => {
          const data = doc.data() || {}
          list.push({ id: doc.id, ...data })
        })
        // Chronimy przed race condition: jeśli w międzyczasie user się zmienił, nie nadpisujmy.
        if (this.clientInvoicesUserId === userId) {
          this.clientInvoices = list
          this.clientInvoicesLoading = false
        }
      } catch (e) {
        console.error('Błąd pobierania faktur klienta:', e)
        if (this.clientInvoicesUserId === userId) {
          this.clientInvoices = []
          this.clientInvoicesLoading = false
        }
      }
    },

    async downloadUsers() {
      try {
        const usersDocs = await db.collection('UsersData').where('fuelOrdersEnabled', '==', true).orderBy('company.name', 'asc').get()
        this.usersList = []
        usersDocs.forEach((doc) => {
          this.usersList.push({
            id: doc.id,
            ...doc.data()
          })
        })
        if (this.usersList.length > 0) {
          this.pickedUser = this.usersList[0]
          this.applyUserToClient(this.pickedUser)
        }
      } catch (e) {
        console.error('downloadUsers error', e)
      }
    },

    async loadRegions() {
      try {
        const doc = await db.collection('routeRegions').doc('regions').get()
        if (doc.exists) {
          const data = doc.data()
          this.regions = Array.isArray(data.regions) ? data.regions : []
        } else {
          this.regions = []
        }
      } catch (e) {
        console.error('Błąd pobierania regionów:', e)
        this.regions = []
      }
    },

    applyUserToClient(user) {
      if (!user) return
      if (this.clientSource !== 'existing') return

      // Reset delivery address selection
      this.selectedDeliveryAddressIndex = null
      this.form.deliveryAddress.address = ''
      this.form.deliveryAddress.city = ''
      this.form.deliveryAddress.postCode = ''
      this.form.deliveryAddress.regionName = ''

      // Pobierz historię faktur dla ratingu płatniczego.
      this.loadInvoicesForUser(user.id)
    },

    selectDeliveryAddress(index) {
      this.selectedDeliveryAddressIndex = index
      if (index === null) {
        // "Wpisz ręcznie"
        this.form.deliveryAddress.address = ''
        this.form.deliveryAddress.city = ''
        this.form.deliveryAddress.postCode = ''
        this.form.deliveryAddress.regionName = ''
        return
      }
      const addr = this.pickedUser?.deliveryAddresses?.[index]
      if (!addr) return
      this.form.deliveryAddress.address = addr.address || ''
      this.form.deliveryAddress.city = addr.city || ''
      this.form.deliveryAddress.postCode = addr.postCode || ''
      this.form.deliveryAddress.regionName = addr.regionName || ''
    },

    resetErrors() {
      this.errors.clientExisting = false
      this.errors.clientName = false
      this.errors.clientNip = false
      this.errors.clientAddress = false
      this.errors.clientPostCode = false
      this.errors.clientCity = false
      this.errors.clientEmail = false
      this.errors.clientPhone = false
      this.errors.deliveryAddressField = false
      this.errors.deliveryAddressMessage = 'Podaj adres dostawy.'
      this.errors.deliveryPostCode = false
      this.errors.deliveryCity = false
      this.errors.fuel = false
      this.errors.quantityLiters = false
      this.errors.deliveryDate = false
    },

    isValidEmail(email) {
      if (!email) return false
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email.trim())
    },

    validate() {
      this.resetErrors()
      let errs = 0

      if (this.clientSource === 'existing') {
        if (!this.pickedUser) {
          this.errors.clientExisting = true
          errs++
        }
      } else {
        const c = this.form.clientManual

        if (!c.invoiceAddress.name || !c.invoiceAddress.name.trim()) {
          this.errors.clientName = true
          errs++
        }
        if (!c.invoiceAddress.nip || !c.invoiceAddress.nip.trim()) {
          this.errors.clientNip = true
          errs++
        }
        if (!c.invoiceAddress.address || !c.invoiceAddress.address.trim()) {
          this.errors.clientAddress = true
          errs++
        }
        if (!c.invoiceAddress.postCode || !c.invoiceAddress.postCode.trim()) {
          this.errors.clientPostCode = true
          errs++
        }
        if (!c.invoiceAddress.city || !c.invoiceAddress.city.trim()) {
          this.errors.clientCity = true
          errs++
        }
        if (!this.isValidEmail(c.contact.email)) {
          this.errors.clientEmail = true
          errs++
        }
        if (!c.contact.phone || c.contact.phone.trim().length < 5) {
          this.errors.clientPhone = true
          errs++
        }
      }

      const d = this.form.deliveryAddress
      if (!d.address || !d.address.trim()) {
        this.errors.deliveryAddressField = true
        errs++
      } else if (!isValidDeliveryAddress(d.address)) {
        // PUESC SENT_105 wymaga osobno HouseNumber. Adres bez numeru → NPP rejekcja.
        this.errors.deliveryAddressField = true
        this.errors.deliveryAddressMessage = 'Adres musi zawierać numer domu (np. "Targowa 9"). Wymagane przez PUESC.'
        errs++
      }
      if (!d.postCode || !d.postCode.trim()) {
        this.errors.deliveryPostCode = true
        errs++
      }
      if (!d.city || !d.city.trim()) {
        this.errors.deliveryCity = true
        errs++
      }

      if (
        !this.deliveryRange ||
        !Array.isArray(this.deliveryRange) ||
        this.deliveryRange.length !== 2 ||
        !this.deliveryRange[0] ||
        !this.deliveryRange[1]
      ) {
        this.errors.deliveryDate = true
        errs++
      }

      if (!this.form.orderedFuel.fuelId) {
        this.errors.fuel = true
        errs++
      }

      const q = Number(this.form.orderedFuel.quantityLiters)
      if (
        this.form.orderedFuel.quantityLiters === null ||
        this.form.orderedFuel.quantityLiters === '' ||
        !Number.isFinite(q) ||
        q < 1 ||
        q > 99999
      ) {
        this.errors.quantityLiters = true
        errs++
      }

      return errs === 0
    },

    buildDeliveryDate() {
      if (
        !this.deliveryRange ||
        !Array.isArray(this.deliveryRange) ||
        this.deliveryRange.length !== 2
      ) {
        return {
          from: { asText: null, unix: null },
          to: { asText: null, unix: null }
        }
      }

      const [fromRaw, toRaw] = this.deliveryRange
      const from = new Date(fromRaw)
      const to = new Date(toRaw)

      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
        return {
          from: { asText: null, unix: null },
          to: { asText: null, unix: null }
        }
      }

      const pad = (n) => n.toString().padStart(2, '0')

      const fromDay = pad(from.getDate())
      const fromMonth = pad(from.getMonth() + 1)
      const fromYear = from.getFullYear()
      const fromAsText = `${fromDay}.${fromMonth}.${fromYear}`
      const fromUnix = Math.floor(from.getTime() / 1000)

      const toDay = pad(to.getDate())
      const toMonth = pad(to.getMonth() + 1)
      const toYear = to.getFullYear()
      const toAsText = `${toDay}.${toMonth}.${toYear}`
      const toUnix = Math.floor(to.getTime() / 1000)

      return {
        from: { asText: fromAsText, unix: fromUnix },
        to: { asText: toAsText, unix: toUnix }
      }
    },

    async geocodeDeliveryAddress() {
      const res = await axios.post(`${this.$store.state.apiLink}/geocodeAddress`, {
        address: this.form.deliveryAddress.address,
        city: this.form.deliveryAddress.city,
        postCode: this.form.deliveryAddress.postCode
      })
      return res.data || { success: false, log: 'Brak odpowiedzi z serwera.' }
    },

    addressKey() {
      const d = this.form.deliveryAddress
      return `${(d.address || '').trim().toLowerCase()}|${(d.city || '').trim().toLowerCase()}|${(d.postCode || '').trim()}`
    },

    resetDeliveryVerification() {
      this.deliveryVerification.loading = false
      this.deliveryVerification.coordinates = null
      this.deliveryVerification.formattedAddress = null
      this.deliveryVerification.mapEmbedUrl = null
      this.deliveryVerification.error = null
      this.deliveryVerification.verifiedFor = null
    },

    scheduleGeocode() {
      // Unieważnij poprzedni wynik przy każdej zmianie adresu.
      this.deliveryVerification.coordinates = null
      this.deliveryVerification.formattedAddress = null
      this.deliveryVerification.mapEmbedUrl = null
      this.deliveryVerification.error = null
      this.deliveryVerification.verifiedFor = null

      if (this.geocodeDebounceTimer) clearTimeout(this.geocodeDebounceTimer)
      const d = this.form.deliveryAddress
      if (!(d.address || '').trim() || !(d.city || '').trim()) {
        this.deliveryVerification.loading = false
        return
      }
      this.deliveryVerification.loading = true
      this.geocodeDebounceTimer = setTimeout(() => { this.runGeocodeVerification() }, 700)
    },

    async runGeocodeVerification() {
      const keyAtStart = this.addressKey()
      try {
        const geo = await this.geocodeDeliveryAddress()
        // Ignoruj wynik jeśli użytkownik w międzyczasie zmienił adres.
        if (this.addressKey() !== keyAtStart) return
        if (!geo.success || !geo.data) {
          this.deliveryVerification.loading = false
          this.deliveryVerification.error = geo.log || 'Nie udało się zweryfikować adresu.'
          return
        }
        this.deliveryVerification.loading = false
        this.deliveryVerification.error = null
        this.deliveryVerification.coordinates = {
          latitude: geo.data.latitude,
          longitude: geo.data.longitude
        }
        this.deliveryVerification.formattedAddress = geo.data.formattedAddress || null
        this.deliveryVerification.mapEmbedUrl = geo.data.mapEmbedUrl || null
        this.deliveryVerification.verifiedFor = keyAtStart
      } catch (e) {
        if (this.addressKey() !== keyAtStart) return
        console.error('Geocoding error:', e)
        this.deliveryVerification.loading = false
        this.deliveryVerification.error = 'Błąd połączenia z usługą geokodowania.'
      }
    },

    async validateAndSave() {
      if (!this.validate()) return
      this.loading = true

      // Wymagamy poprawnych koordynatów w granicach PL (SENT-105). Geocoding blokuje zapis jeśli zawiedzie.
      let coordinates
      try {
        // Użyj cache z weryfikacji jeśli dotyczy aktualnego adresu.
        if (this.deliveryVerification.verifiedFor === this.addressKey() && this.deliveryVerification.coordinates) {
          coordinates = {
            latitude: this.deliveryVerification.coordinates.latitude,
            longitude: this.deliveryVerification.coordinates.longitude
          }
        } else {
          const geo = await this.geocodeDeliveryAddress()
          if (!geo.success || !geo.data) {
            this.$store.commit('setNotification', {
              show: true,
              head: 'Nie udało się zweryfikować adresu dostawy',
              subheader: geo.log || 'Sprawdź adres i spróbuj ponownie.',
              success: false
            })
            this.loading = false
            return
          }
          coordinates = {
            latitude: geo.data.latitude,
            longitude: geo.data.longitude
          }
        }
      } catch (e) {
        console.error('Geocoding error:', e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd połączenia z usługą geokodowania',
          subheader: 'Spróbuj ponownie za chwilę.',
          success: false
        })
        this.loading = false
        return
      }

      try {
        const docRef = db.collection('FuelOrders').doc()
        const idDoc = docRef.id

        let clientObj
        if (this.clientSource === 'existing' && this.pickedUser) {
          const u = this.pickedUser
          clientObj = {
            contact: {
              email: u.contact?.email || '',
              invoiceEmail: u.contact?.invoiceEmail || '',
              phone: u.contact?.phone || ''
            },
            invoiceAddress: {
              address: u.company?.address || '',
              city: u.company?.city || '',
              name: u.company?.name || '',
              nip: u.company?.nip || '',
              postCode: u.company?.postCode || ''
            },
            systemUserId: u.id || null
          }
        } else {
          const c = this.form.clientManual
          clientObj = {
            contact: {
              email: c.contact.email || '',
              invoiceEmail: c.contact.invoiceEmail || '',
              phone: c.contact.phone || ''
            },
            invoiceAddress: {
              address: c.invoiceAddress.address || '',
              city: c.invoiceAddress.city || '',
              name: c.invoiceAddress.name || '',
              nip: c.invoiceAddress.nip || '',
              postCode: c.invoiceAddress.postCode || ''
            },
            systemUserId: null
          }
        }

        const deliveryAddress = {
          address: this.form.deliveryAddress.address || '',
          city: this.form.deliveryAddress.city || '',
          postCode: this.form.deliveryAddress.postCode || '',
          regionName: this.form.deliveryAddress.regionName || null,
          coordinates: coordinates
        }

        const now = new Date()
        const unix = Math.floor(now.getTime() / 1000)
        const year = now.getFullYear()
        const monthNum = now.getMonth() + 1
        const dayNum = now.getDate()

        const pad = (n) => n.toString().padStart(2, '0')
        const asText = `${pad(dayNum)}.${pad(monthNum)}.${year}`

        const hours = now.getHours()
        const minutes = now.getMinutes()
        const tzOffsetMin = now.getTimezoneOffset()
        const offSign = tzOffsetMin <= 0 ? '+' : '-'
        const offAbs = Math.abs(tzOffsetMin)
        const offH = Math.floor(offAbs / 60)
        const offM = offAbs % 60
        // ISO 8601 wymaga `YYYY-MM-DDTHH:mm:ss±HH:MM` (rok-miesiąc-dzień), nie polskiego
        // dd.mm.yyyy. Wcześniej zapis był broken (`22.04.2026T...`) — niczego nie crashował,
        // ale uniemożliwiał parsowanie. asText (dd.mm.yyyy) zostaje w osobnym polu.
        const dateLocal = `${year}-${pad(monthNum)}-${pad(dayNum)}T${pad(hours)}:${pad(minutes)}:00${offSign}${pad(offH)}:${pad(offM)}`

        const ud = this.$store?.state?.userData || {}
        const creatorId = ud.id || null
        const creatorName = `${ud.name || ''} ${ud.surname || ''}`.trim() || 'Nieznany użytkownik'

        const meta = {
          createdDate: {
            asText,
            date: dateLocal,
            dayNum,
            monthNum,
            unix,
            year
          },
          creator: {
            id: creatorId,
            name: creatorName
          }
        }

        const fuelId = this.form.orderedFuel.fuelId
        const fuel = this.fuels.find((f) => f.id === fuelId)
        const fuelName = fuel ? fuel.name : ''

        const quantityLiters = Number(this.form.orderedFuel.quantityLiters)
        const deliveryDate = this.buildDeliveryDate()

        const payload = {
          status: this.form.acceptedByAdmin ? 2 : 1,
          canceled: false,
          acceptedByAdmin: !!this.form.acceptedByAdmin,
          client: clientObj,
          createdByClient: false,
          delivered: false,
          deliveryAddress,
          deliveryDate,
          finalDeliveryDate: null,
          id: idDoc,
          invoice: {
            invoiceId: null,
            invoiceNumber: null,
            invoiced: false,
            isLauronInvoice: !!this.form.invoice.isLauronInvoice
          },
          meta,
          orderedFuel: {
            fuelId,
            fuelName,
            quantityLiters
          },
          unloadedFuel: null,
          isInRoute: false,
          routeRef: null
        }

        await docRef.set(payload, { merge: false })

        this.$store.commit('setNotification', {
          show: true,
          head: 'Akcja zakończona sukcesem!',
          subheader: 'Zamówienie paliwowe zostało dodane.',
          success: true
        })

        this.emitClose()
      } catch (e) {
        console.error(e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd zapisu do bazy',
          subheader: 'Spróbuj ponownie lub skontaktuj się z administratorem.',
          success: false
        })
        this.loading = false
      }
    },

    clear() {
      this.loading = false
      this.resetErrors()
      this.clientSource = 'existing'
      this.selectedDeliveryAddressIndex = null
      this.clientInvoices = []
      this.clientInvoicesUserId = null
      this.clientInvoicesLoading = false
      this.showInvoicesModal = false
      this.pickedUser = this.usersList[0] || null
      if (this.pickedUser) this.applyUserToClient(this.pickedUser)

      this.deliveryRange = null

      this.form = {
        acceptedByAdmin: true,
        clientManual: {
          contact: {
            email: '',
            invoiceEmail: '',
            phone: ''
          },
          invoiceAddress: {
            address: '',
            city: '',
            name: '',
            nip: '',
            postCode: ''
          }
        },
        deliveryAddress: {
          address: '',
          city: '',
          postCode: '',
          regionName: ''
        },
        invoice: {
          isLauronInvoice: true
        },
        orderedFuel: {
          fuelId: 30,
          quantityLiters: null
        }
      }
    },

    emitClose() {
      this.$emit('closeOrderModal')
      this.clear()
    }
  }
}
</script>

<style lang="scss" scoped>
.modalShell {
  height: 100%;
  background: rgba(30, 41, 59, 0.98);
  border-left: 1px solid rgba(71, 85, 105, 0.8);
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.35);
}

.modalHeader {
  padding: 20px 20px 18px 20px;
  border-bottom: 1px solid rgba(51, 65, 85, 1);
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.92));
}

.sectionCard {
  overflow: visible;
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 22px;
  background: rgba(30, 41, 59, 0.72);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  position: relative;
}

.sectionHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 14px 16px;
  border-bottom: 1px solid rgba(51, 65, 85, 0.9);
  background: rgba(51, 65, 85, 0.32);
}

.sectionTitle {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

.sectionSub {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.5;
  color: rgba(148, 163, 184, 1);
}

.sectionBody {
  padding: 16px;
}

.tabSwitchBtn {
  padding: 10px 14px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(148, 163, 184, 1);
  transition: all 0.18s ease;
}

.tabSwitchBtn:hover {
  background: rgba(51, 65, 85, 0.7);
  color: #e2e8f0;
}

.tabSwitchBtn--active {
  background: rgba(51, 65, 85, 1);
  color: #ffffff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
}

.fieldWrap {
  display: flex;
  flex-direction: column;
}

.fieldLabel {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #e2e8f0;
}

.fieldHint {
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.5;
  color: rgba(148, 163, 184, 1);
}

.fieldError {
  margin-top: 6px;
  font-size: 12px;
  color: #f87171;
}

.inputField,
.selectField,
.selectBtn {
  display: block;
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 14px;
  font-size: 14px;
  line-height: 1.3;
  transition: all 0.18s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.inputField::placeholder {
  color: rgba(148, 163, 184, 0.75);
}

.inputField:focus,
.selectField:focus,
.selectBtn:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.inputField:disabled,
.selectField:disabled,
.selectBtn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: rgba(15, 23, 42, 0.45);
}

.inputFieldError {
  border-color: rgba(239, 68, 68, 0.9);
}

.pickedFromListHint {
  margin-top: -4px;
  font-size: 10px;
  line-height: 1.2;
  color: rgba(148, 163, 184, 0.9);
  font-style: italic;
}

.addrVerifyBox {
  border-radius: 14px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.4;
  border: 1px solid rgba(100, 116, 139, 0.45);
  background: rgba(15, 23, 42, 0.55);
  color: #e2e8f0;
}

.addrVerifyLoading {
  color: #cbd5e1;
}

.addrVerifyError {
  border-color: rgba(239, 68, 68, 0.55);
  background: rgba(127, 29, 29, 0.25);
  color: #fecaca;
}

.addrVerifySuccess {
  border-color: rgba(16, 185, 129, 0.45);
  background: rgba(6, 78, 59, 0.25);
}

.selectBtn {
  position: relative;
  text-align: left;
  cursor: default;
  padding-right: 40px;
}

.selectOptions {
  position: absolute;
  z-index: 20;
  margin-top: 8px;
  max-height: 15rem;
  width: 100%;
  overflow: auto;
  border-radius: 16px;
  border: 1px solid rgba(71, 85, 105, 0.8);
  background: rgba(15, 23, 42, 0.98);
  padding: 6px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.32);
  font-size: 14px;
}

.infoGrid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.infoBox {
  border: 1px solid rgba(71, 85, 105, 0.75);
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.45);
  padding: 14px;
}

.infoLabel {
  font-size: 11px;
  font-weight: 700;
  color: rgba(148, 163, 184, 1);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.infoValue {
  margin-top: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

.infoMuted {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.55;
  color: #cbd5e1;
}

.ratingCard {
  border: 1px solid rgba(71, 85, 105, 0.75);
  border-radius: 18px;
  background: rgba(15, 23, 42, 0.45);
  padding: 14px 16px;
}

.ratingTop {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.ratingHeading {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  color: #ffffff;
  text-transform: uppercase;
}

.ratingSub {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.45;
  color: rgba(148, 163, 184, 1);
}

.ratingBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 700;
  border: 1px solid rgba(59, 130, 246, 0.55);
  background: rgba(37, 99, 235, 0.22);
  color: #dbeafe;
  transition: all 0.18s ease;
  white-space: nowrap;
}

.ratingBtn:hover {
  background: rgba(37, 99, 235, 0.3);
}

.ratingBtn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.ratingStarsRow {
  margin-top: 12px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.starsWrap {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.starIcon {
  height: 20px;
  width: 20px;
  fill: rgba(71, 85, 105, 0.95);
  transition: fill 0.18s ease;
}

.starIcon--on {
  fill: #facc15;
}

.starsWrap--empty .starIcon {
  fill: rgba(51, 65, 85, 0.85);
}

.ratingValue {
  font-size: 13px;
  font-weight: 700;
  color: #f8fafc;
}

.ratingValueHint {
  margin-left: 4px;
  font-weight: 500;
  color: rgba(148, 163, 184, 1);
}

.ratingGrid {
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

@media (min-width: 520px) {
  .ratingGrid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.metricBox {
  border: 1px solid rgba(71, 85, 105, 0.55);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.55);
  padding: 10px 12px;
}

.metricLabel {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(148, 163, 184, 1);
}

.metricValue {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 700;
  color: #f8fafc;
}

.metricValue--danger {
  color: #fca5a5;
}

.metricSub {
  margin-top: 4px;
  font-size: 10px;
  color: rgba(148, 163, 184, 0.95);
}

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  transition: all 0.18s ease;
}

.iconBtn:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnSecondary,
.btnPrimary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.18s ease;
}

.btnSecondary {
  border: 1px solid rgba(100, 116, 139, 0.8);
  background: rgba(51, 65, 85, 0.75);
  color: #f1f5f9;
}

.btnSecondary:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnPrimary {
  border: 1px solid rgba(59, 130, 246, 0.55);
  background: rgba(37, 99, 235, 0.22);
  color: #dbeafe;
}

.btnPrimary:hover {
  background: rgba(37, 99, 235, 0.3);
}

.backdrop-soft {
  backdrop-filter: blur(2px);
}

/* DATEPICKER — skopiowane z RouteCreate, pełny zestaw reguł łapiący wszystkie stany */
.datePickerWrap {
  position: relative;
  z-index: 80;
}

:deep(.dp__main) {
  width: 100%;
}

:deep(.dp__input_wrap) {
  width: 100%;
}

:deep(.dp__input) {
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 36px 11px 38px;
  font-size: 14px;
  line-height: 1.3;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
}

:deep(.dp__input::placeholder) {
  color: rgba(148, 163, 184, 0.75);
}

:deep(.dp__input_focus) {
  border-color: rgba(99, 102, 241, 0.85) !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15) !important;
}

:deep(.dp__icon),
:deep(.dp__clear_icon) {
  color: rgba(148, 163, 184, 0.9);
}

:deep(.dp__menu) {
  z-index: 120 !important;
  margin-top: 8px;
  border-radius: 16px;
  border: 1px solid rgba(71, 85, 105, 0.85);
  background: rgba(15, 23, 42, 0.98) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}

:deep(.dp__menu_inner),
:deep(.dp__instance_calendar),
:deep(.dp__calendar),
:deep(.dp__calendar_wrap),
:deep(.dp__overlay),
:deep(.dp__selection_preview),
:deep(.dp__time_picker),
:deep(.dp__time_picker_inline_container),
:deep(.dp__time_picker_overlay_container),
:deep(.dp__time_picker_overlay),
:deep(.dp__action_row),
:deep(.dp__month_year_row),
:deep(.dp__calendar_header),
:deep(.dp__calendar_header_item),
:deep(.dp__month_year_wrap) {
  background: rgba(15, 23, 42, 0.98) !important;
  color: #f8fafc !important;
}

:deep(.dp__arrow_top) {
  display: none !important;
}

:deep(.dp__month_year_select) {
  color: #f8fafc !important;
}

:deep(.dp__btn),
:deep(.dp__icon_btn),
:deep(.dp__inner_nav),
:deep(.dp__inc_dec_button),
:deep(.dp__pm_am_button),
:deep(.dp__overlay_action),
:deep(.dp__tp_inline_btn),
:deep([data-test="tp-btn"]),
:deep(.dp__time_picker_inline_btn) {
  color: #cbd5e1 !important;
  background: transparent !important;
  border: none !important;
}

:deep(.dp__btn:hover),
:deep(.dp__icon_btn:hover),
:deep(.dp__inner_nav:hover),
:deep(.dp__inc_dec_button:hover),
:deep(.dp__pm_am_button:hover),
:deep(.dp__overlay_action:hover),
:deep(.dp__tp_inline_btn:hover),
:deep([data-test="tp-btn"]:hover),
:deep(.dp__time_picker_inline_btn:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
  border: none !important;
}

:deep(.dp__week_day) {
  color: rgba(148, 163, 184, 1) !important;
  font-weight: 600;
}

:deep(.dp__calendar_item) {
  color: #e2e8f0 !important;
}

:deep(.dp__cell_inner) {
  border-radius: 10px !important;
  color: inherit !important;
  box-shadow: none !important;
  border: 0 !important;
}

:deep(.dp__cell_inner:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

:deep(.dp__range_start),
:deep(.dp__range_end),
:deep(.dp__active_date) {
  background: rgba(37, 99, 235, 0.95) !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__range_between) {
  background: rgba(37, 99, 235, 0.18) !important;
  color: #dbeafe !important;
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__range_between .dp__cell_inner) {
  background: transparent !important;
  color: #dbeafe !important;
  border: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

:deep(.dp__range_between:hover),
:deep(.dp__range_between:hover .dp__cell_inner),
:deep(.dp__range_between.dp__date_hover),
:deep(.dp__range_between.dp__date_hover .dp__cell_inner) {
  background: rgba(37, 99, 235, 0.24) !important;
  color: #ffffff !important;
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__today) {
  border: 1px solid rgba(59, 130, 246, 0.65) !important;
}

:deep(.dp__cell_disabled),
:deep(.dp__disabled),
:deep(.dp__disabled .dp__cell_inner),
:deep(.dp__calendar_item.dp__cell_disabled .dp__cell_inner) {
  color: rgba(100, 116, 139, 0.6) !important;
  opacity: 0.45 !important;
  cursor: not-allowed !important;
}

:deep(.dp__time_display),
:deep(.dp__time_col),
:deep(.dp__time_input),
:deep(.dp__time_input input),
:deep(.dp__overlay_cell),
:deep(.dp__overlay_col),
:deep(.dp__overlay_cell_active),
:deep(.dp__overlay_cell_pad),
:deep(.dp__time_picker_item),
:deep(.dp__time_col_reg),
:deep(.dp__time_col_reg_block),
:deep(.dp__time_col_reg_inline) {
  color: #f8fafc !important;
  background: transparent !important;
}

:deep(.dp__time_display) {
  font-weight: 700 !important;
}

:deep(.dp__overlay_cell:hover),
:deep(.dp__overlay_col:hover),
:deep(.dp__time_display:hover),
:deep(.dp__time_col_reg_block:hover),
:deep(.dp__time_col_reg_inline:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

:deep(.dp__action_row) {
  border-top: 1px solid rgba(71, 85, 105, 0.7) !important;
}

:deep(.dp__action_button) {
  border-radius: 12px !important;
}

:deep(.dp__action_select) {
  background: rgba(37, 99, 235, 0.95) !important;
  color: #ffffff !important;
}

:deep(.dp__action_cancel) {
  color: #cbd5e1 !important;
  background: transparent !important;
}

:deep(.dp__action_cancel:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

:deep(.dp__selection_preview) {
  color: #94a3b8 !important;
}

:deep(.dp__menu *:hover) {
  color: inherit;
}

:deep(.dp__menu button:hover),
:deep(.dp__menu [role="button"]:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

@media (min-width: 640px) {
  .modalHeader {
    padding: 24px 24px 20px 24px;
  }

  .sectionBody,
  .sectionHead {
    padding-left: 18px;
    padding-right: 18px;
  }

  .infoGrid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>